// ChapterQuiz.jsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Options from "./Options";
import "./Quiz.css";
import API_BASE_URL from "../../config/api";

const ChapterQuiz = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();

  const [startTime, setStartTime] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(0);

  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [confirmedAnswers, setConfirmedAnswers] = useState([]);
  const [visitedQuestions, setVisitedQuestions] = useState(new Set([0]));

  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.id;

  /* ======================================================
     🚫 NAVIGATION BLOCK (FIXED & WORKING)
     ====================================================== */
useEffect(() => {
  if (!quiz?.questions?.length) return;

  // Force back button to trigger popstate
  window.history.pushState(null, "", window.location.href);
  window.history.pushState(null, "", window.location.href);

  const handlePopState = () => {
    window.history.pushState(null, "", window.location.href);

    Swal.fire({
      icon: "warning",
      title: "Navigation Blocked",
      text: "You cannot leave the quiz without submitting it.",
      confirmButtonText: "OK",
    });
  };

  // ONLY reliable way to stop reload / close
  const handleBeforeUnload = (e) => {
    e.preventDefault();
    e.returnValue =
      "Exam in progress. Leaving will submit or cancel your exam.";
  };

  // Keyboard refresh only
  const handleKeyDown = (e) => {
    if (
      e.key === "F5" ||
      (e.ctrlKey && e.key.toLowerCase() === "r")
    ) {
      e.preventDefault();
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Refreshing is not allowed during the exam",
        showConfirmButton: false,
        timer: 2000,
      });
    }
  };

  window.addEventListener("popstate", handlePopState);
  window.addEventListener("beforeunload", handleBeforeUnload);
  window.addEventListener("keydown", handleKeyDown);

  return () => {
    window.removeEventListener("popstate", handlePopState);
    window.removeEventListener("beforeunload", handleBeforeUnload);
    window.removeEventListener("keydown", handleKeyDown);
  };
}, [quiz]);


  /* ================= FETCH QUIZ ================= */
  useEffect(() => {
    if (!userId) {
      Swal.fire("Not Logged In", "Please login first", "warning")
        .then(() => navigate("/login"));
      return;
    }

    fetch(
      `${API_BASE_URL}/chapterquiz.php?chapter_id=${chapterId}&user_id=${userId}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) {
          Swal.fire("Error", data.message || "Failed to load quiz", "error");
          return;
        }

        const questions = (data.questions || []).map((q) => ({
          id: q.id,
          question: q.question,
          type: q.type || "mcq",
          options: q.options
            ? q.options
            : [
                q.option1,
                q.option2,
                q.option3,
                q.option4,
                q.option5,
              ].filter(Boolean),
        }));

        setStartTime(data.start_time);
        setQuiz({ ...data, questions });
      });
  }, [chapterId, userId, navigate]);

  const question = quiz?.questions?.[activeQuestion];

  /* ================= HELPERS ================= */
  const isSkipped = (index) =>
    userAnswers.find((a) => a.questionIndex === index)?.skipped;

  const goToQuestion = (index) => {
    setVisitedQuestions((prev) => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
    setActiveQuestion(index);
  };

  useEffect(() => {
    const ua = userAnswers.find((a) => a.questionIndex === activeQuestion);
    setSelectedAnswer(ua?.selectedAnswerIndex ?? null);
  }, [activeQuestion, userAnswers]);

  /* ================= ANSWER SELECT ================= */
  const handleAnswerSelect = (index) => {
    if (bookmarks.includes(activeQuestion)) return;

    setSelectedAnswer(index);

    setUserAnswers((prev) => {
      const exists = prev.find((a) => a.questionIndex === activeQuestion);
      if (exists) {
        return prev.map((a) =>
          a.questionIndex === activeQuestion
            ? { ...a, selectedAnswerIndex: index, attempted: true, skipped: false }
            : a
        );
      }
      return [
        ...prev,
        {
          questionIndex: activeQuestion,
          selectedAnswerIndex: index,
          attempted: true,
          skipped: false,
        },
      ];
    });

    setConfirmedAnswers((prev) =>
      prev.includes(activeQuestion) ? prev : [...prev, activeQuestion]
    );
  };

  /* ================= BOOKMARK ================= */
  const toggleBookmark = () => {
    setBookmarks((prev) =>
      prev.includes(activeQuestion)
        ? prev.filter((q) => q !== activeQuestion)
        : [...prev, activeQuestion]
    );

    setSelectedAnswer(null);
    setUserAnswers((prev) =>
      prev.filter((a) => a.questionIndex !== activeQuestion)
    );
    setConfirmedAnswers((prev) =>
      prev.filter((q) => q !== activeQuestion)
    );
  };

  /* ================= NEXT / PREVIOUS ================= */
  const handleNext = () => {
    const ua = userAnswers.find((a) => a.questionIndex === activeQuestion);
    if (
      visitedQuestions.has(activeQuestion) &&
      !ua &&
      !bookmarks.includes(activeQuestion)
    ) {
      setUserAnswers((prev) => [
        ...prev,
        { questionIndex: activeQuestion, skipped: true },
      ]);
    }
    goToQuestion(
      Math.min(activeQuestion + 1, quiz.questions.length - 1)
    );
  };

  const handlePrevious = () => {
    goToQuestion(Math.max(activeQuestion - 1, 0));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    const confirm = await Swal.fire({
      title: "Finish Exam?",
      icon: "warning",
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;

    const answersPayload = quiz.questions.map((q, idx) => {
      const ua = userAnswers.find((a) => a.questionIndex === idx);
      let review = "NA";
      if (ua?.skipped) review = "S";
      else if (ua?.attempted) review = "A";
      else if (bookmarks.includes(idx)) review = "B";

      return {
        question_id: q.id,
        selected_option:
          ua?.selectedAnswerIndex != null
            ? `option${ua.selectedAnswerIndex + 1}`
            : "",
        review,
      };
    });

    const res = await fetch(
      `${API_BASE_URL}/submit_answer.php`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          chapter_id: chapterId,
          exam_type: "C",
          answers: answersPayload,
          start_time: startTime,
        }),
      }
    );

    const data = await res.json();
    if (data.success) {
      navigate(`/finish-exam/${data.start_exam_id}`);
    }
  };

  /* ================= UI ================= */
  return (
    <section>
    <div className="chapter-quiz-wrapper">
      <div className="quiz-header">
        <h6>{quiz?.subject?.name} / {quiz?.chapter?.name}</h6>
        <div>{activeQuestion + 1} / {quiz?.questions?.length}</div>
      </div>

      {question && (
        <>
          <div className="question-grid">
            {quiz.questions.map((_, i) => (
              <div
                key={i}
                className={`question-box
                  ${i === activeQuestion ? "current" : ""}
                  ${confirmedAnswers.includes(i) ? "confirmed" : ""}
                  ${bookmarks.includes(i) ? "bookmarked" : ""}
                  ${isSkipped(i) ? "skipped" : ""}`}
                onClick={() => goToQuestion(i)}
              >
                {i + 1}
              </div>
            ))}
          </div>

          <h3 className="question-text">
            {activeQuestion + 1}. {question.question}
          </h3>
 
              <div className="question-meta">
  <span
    style={{
      color: "rgb(6 75 169)",
      fontWeight: "bold"
    }}
  >
    Quiz ID:
  </span>

  {" "}

  <strong
    style={{
      color: "rgb(6 75 169)"
    }}
  >
    {question.id}
  </strong>
</div>
          <Options
            question={question}
            selectedAnswer={selectedAnswer}
            onSelect={handleAnswerSelect}
          />

          <div className="quiz-actions-row">
            <button className="exam-btn" onClick={handlePrevious}>← Previous</button>
            <button className="exam-btn" onClick={handleNext}>Next →</button>
            <button className="exam-btn" onClick={toggleBookmark}>
              {bookmarks.includes(activeQuestion) ? "Unmark" : "Bookmark"}
            </button>
            <button className="exam-btn-submit" onClick={handleSubmit}>
              Finish Exam
            </button>
          </div>
        </>
      )}
    </div>
    </section>
  );
};

export default ChapterQuiz;
